import { z } from 'zod';
import { Email, IsoDateTime, LocalDate, PageQuery, Uuid, page, patchOf } from './common.js';
/**
 * Module `compliance`: contractor documents and inductions (docs/modules/compliance.md, ADR 0025, spec 8.2a).
 * Owner: task S28 (documents) and S28b (the Induction* shapes). Builders may add optional fields.
 */
// ---------- Document types and requirements ----------
export const DocumentType = z.object({
    id: Uuid,
    name: z.string(),
    description: z.string(),
    needsIssueDate: z.boolean(),
    needsExpiry: z.boolean(),
    needsFile: z.boolean(),
    /** Reminder emails this many days before expiry, e.g. [30, 7]; [] = none. */
    reminderDays: z.array(z.number().int().min(1).max(365)),
    active: z.boolean(),
});
export const DocumentTypeCreate = z.object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).default(''),
    needsIssueDate: z.boolean().default(false),
    needsExpiry: z.boolean().default(true),
    needsFile: z.boolean().default(true),
    reminderDays: z.array(z.number().int().min(1).max(365)).max(5).default([30, 7]),
});
export const DocumentTypeUpdate = patchOf(DocumentTypeCreate).extend({ active: z.boolean().optional() });
export const RequirementMode = z.enum(['refuse', 'warn']);
/** PUT /compliance/groups/:groupId/requirements */
export const GroupRequirements = z.object({
    requirements: z.array(z.object({ typeId: Uuid, mode: RequirementMode })).max(30),
});
// ---------- Documents ----------
export const DocumentStatus = z.enum(['pending_review', 'valid', 'rejected', 'superseded']);
export const ComplianceDocument = z.object({
    id: Uuid,
    memberId: Uuid,
    typeId: Uuid,
    typeName: z.string(),
    fileId: Uuid.nullable(),
    reference: z.string(),
    issueDate: LocalDate.nullable(),
    expiryDate: LocalDate.nullable(),
    status: DocumentStatus,
    uploadedByLabel: z.string(),
    uploadedVia: z.enum(['portal', 'companion', 'prearrival', 'import']),
    reviewedByLabel: z.string().nullable(),
    reviewedAt: IsoDateTime.nullable(),
    rejectReason: z.string().nullable(),
    createdAt: IsoDateTime,
});
export const ComplianceDocumentCreate = z.object({
    typeId: Uuid,
    /** From POST /compliance/uploads (portal) or the app/pre-arrival upload routes. */
    uploadId: Uuid.nullable().default(null),
    reference: z.string().trim().max(120).default(''),
    issueDate: LocalDate.nullable().default(null),
    expiryDate: LocalDate.nullable().default(null),
});
export const ComplianceDocumentReview = z.object({ decision: z.enum(['valid', 'rejected']), reason: z.string().trim().max(500).optional() });
/** One member's standing against their group's requirements (also used for permits, ADR 0026). */
export const ComplianceStatus = z.object({
    memberId: Uuid,
    groupId: Uuid,
    outcome: z.enum(['met', 'warn', 'refuse', 'not_required']),
    items: z.array(z.object({
        typeId: Uuid,
        typeName: z.string(),
        mode: RequirementMode,
        state: z.enum(['valid', 'expiring', 'expired', 'missing', 'pending_review', 'rejected']),
        expiryDate: LocalDate.nullable(),
    })),
    induction: z.object({ required: z.boolean(), passedUntil: IsoDateTime.nullable(), state: z.enum(['valid', 'expired', 'missing', 'not_required']) }),
});
/** GET /compliance/members/:memberId (portal). */
export const ComplianceMemberView = z.object({ status: ComplianceStatus, documents: z.array(ComplianceDocument) });
/** POST .../uploads (portal, Companion app, pre-arrival page): the id to put in `ComplianceDocumentCreate.uploadId`. */
export const ComplianceUploadResult = z.object({ uploadId: Uuid });
/**
 * GET /compliance/me (Companion app) and GET /compliance/prearrival/:token (pre-arrival page): the person's standing, their documents
 * and the document types they are asked for (so the app can offer an upload for each). `selfUpload` false = view only.
 */
export const MyCompliance = z.object({
    status: ComplianceStatus.nullable(),
    documents: z.array(ComplianceDocument),
    types: z.array(DocumentType),
    selfUpload: z.boolean(),
    /** The biggest file the server accepts, in MB (tenant setting `maxDocumentMb`, at most 25). Absent = use the 25 MB ceiling. */
    maxDocumentMb: z.number().int().optional(),
});
export const ComplianceExportQuery = z.object({
    groupId: Uuid.optional(),
    state: z.enum(['all', 'problems', 'expiring']).default('problems'),
    withinDays: z.coerce.number().int().min(1).max(365).default(30),
    format: z.enum(['csv', 'xlsx']).default('csv'),
});
export const ComplianceReportQuery = PageQuery.extend({
    groupId: Uuid.optional(),
    state: z.enum(['all', 'problems', 'expiring']).default('problems'),
    withinDays: z.coerce.number().int().min(1).max(365).default(30),
});
export const ComplianceReportRow = z.object({
    memberId: Uuid,
    memberName: z.string(),
    groupName: z.string(),
    typeName: z.string(),
    state: z.string(),
    expiryDate: LocalDate.nullable(),
});
export const ComplianceReport = page(ComplianceReportRow);
// ---------- Inductions (task S28b) ----------
export const QuizQuestion = z.object({
    id: z.string().min(1).max(40),
    text: z.string().trim().min(1).max(500),
    kind: z.enum(['single', 'multiple']),
    options: z.array(z.object({ id: z.string().min(1).max(40), text: z.string().trim().min(1).max(200) })).min(2).max(6),
    /** Option ids. Sent to kiosks (offline grading, ADR 0025) and never to the Companion app or web pages. */
    correct: z.array(z.string()).min(1),
    explanation: z.string().max(500).default(''),
});
export const Induction = z.object({
    id: Uuid,
    name: z.string(),
    /** Text shown with or instead of the media. */
    body: z.string(),
    mediaFileId: Uuid.nullable(),
    mediaKind: z.enum(['video', 'pdf']).nullable(),
    /** Minimum seconds the video must play before the quiz opens (0 = no minimum). */
    minWatchSeconds: z.number().int(),
    questions: z.array(QuizQuestion),
    passMarkPercent: z.number().int(),
    validityMonths: z.number().int(),
    version: z.number().int(),
    active: z.boolean(),
    /** groupId + siteId (null = every site). */
    assignments: z.array(z.object({ groupId: Uuid, siteId: Uuid.nullable() })),
    updatedAt: IsoDateTime,
});
export const InductionCreate = z.object({
    name: z.string().trim().min(1).max(120),
    body: z.string().max(20000).default(''),
    mediaUploadId: Uuid.nullable().default(null),
    minWatchSeconds: z.number().int().min(0).max(3600).default(0),
    questions: z.array(QuizQuestion).max(30).default([]),
    passMarkPercent: z.number().int().min(0).max(100).default(80),
    validityMonths: z.number().int().min(1).max(60).default(12),
    assignments: z.array(z.object({ groupId: Uuid, siteId: Uuid.nullable() })).max(200).default([]),
});
export const InductionUpdate = patchOf(InductionCreate).extend({ active: z.boolean().optional(), removeMedia: z.boolean().optional() });
/** What a person sees to take it (no correct answers). */
export const InductionToTake = z.object({
    inductionId: Uuid,
    version: z.number().int(),
    name: z.string(),
    body: z.string(),
    media: z.object({ url: z.string(), kind: z.enum(['video', 'pdf']), expiresAt: IsoDateTime }).nullable(),
    minWatchSeconds: z.number().int(),
    questions: z.array(QuizQuestion.omit({ correct: true, explanation: true })),
    passMarkPercent: z.number().int(),
});
export const InductionAttemptSubmit = z.object({
    idempotencyKey: Uuid,
    inductionId: Uuid,
    version: z.number().int(),
    answers: z.record(z.string(), z.array(z.string())),
    watchedSeconds: z.number().int().min(0).default(0),
    occurredAt: IsoDateTime,
});
export const InductionAttemptResult = z.object({
    passed: z.boolean(),
    scorePercent: z.number().int(),
    /** Question ids answered wrongly, so the person can retry. */
    wrong: z.array(z.string()),
    passedUntil: IsoDateTime.nullable(),
});
/** Kiosk step `induction` input (event.steps.induction). */
export const InductionStepInput = InductionAttemptSubmit.omit({ idempotencyKey: true }).extend({ kioskResult: InductionAttemptResult.pick({ passed: true, scorePercent: true }) });
export const InductionPass = z.object({
    id: Uuid,
    inductionId: Uuid,
    inductionName: z.string(),
    version: z.number().int(),
    memberId: Uuid.nullable(),
    visitorName: z.string().nullable(),
    passedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    via: z.enum(['kiosk', 'companion', 'prearrival', 'contactless', 'portal']),
    scorePercent: z.number().int(),
});
export const InductionPassList = page(InductionPass);
export const InductionPassQuery = PageQuery;
/** GET /compliance/inductions/report/export */
export const InductionExportQuery = z.object({ inductionId: Uuid.optional(), format: z.enum(['csv', 'xlsx']).default('csv') });
/** GET /compliance/contactless/inductions: `groupId` (optional) narrows the list to what that group takes at the session's site. */
export const ContactlessInductionQuery = z.object({ groupId: Uuid.optional() });
/** POST /compliance/contactless/inductions/attempts: the visitor has no account, so the name and email make the pass key (visitorInductionKey). */
export const ContactlessInductionAttempt = InductionAttemptSubmit.extend({ visitorName: z.string().trim().min(1).max(120), email: z.string().trim().max(254).nullable().default(null) });
// ---------- Contactless phone ----------
/**
 * What the contactless phone gets of the `compliance` config section: only which induction each group takes. No answers, no members,
 * no visitor passes (those are for the kiosk, which is a paired device). The questions come from GET /compliance/contactless/inductions.
 */
export const ContactlessComplianceSection = z.object({
    groups: z.array(z.object({ groupId: Uuid, inductionId: Uuid.nullable() })),
});
// ---------- Kiosk ----------
/** Kiosk config section `compliance` (ADR 0026). */
export const KioskComplianceSection = z.object({
    /** Kiosks fetch induction videos on Wi-Fi only (tenant setting `kioskVideoWifiOnly`). */
    kioskVideoWifiOnly: z.boolean().default(true),
    groups: z.array(z.object({ groupId: Uuid, requirements: z.array(z.object({ typeId: Uuid, mode: RequirementMode })), inductionId: Uuid.nullable() })),
    /** Per member of the site's groups with requirements: what is missing or expired today. */
    members: z.array(z.object({ memberId: Uuid, missing: z.array(Uuid), inductionUntil: IsoDateTime.nullable() })),
    /** HMAC(siteKey, visitorInductionKey) -> pass expiry, for standard-group visitors. */
    visitorPasses: z.record(z.string(), IsoDateTime),
    /** The inductions themselves, with correct answers (kiosk grading) and a signed media URL to cache. */
    inductions: z.array(z.object({
        id: Uuid,
        version: z.number().int(),
        name: z.string(),
        body: z.string(),
        media: z.object({ fileId: Uuid, url: z.string(), kind: z.enum(['video', 'pdf']) }).nullable(),
        minWatchSeconds: z.number().int(),
        questions: z.array(QuizQuestion),
        passMarkPercent: z.number().int(),
        validityMonths: z.number().int(),
    })),
});
// ---------- Settings ----------
export const ComplianceTenantSettings = z.object({
    maxDocumentMb: z.number().int().min(1).max(25).default(10),
    maxVideoMb: z.number().int().min(10).max(1000).default(300),
    keepReplacedDays: z.number().int().min(0).default(365),
    attemptRetentionDays: z.number().int().min(0).default(365),
    /** Local time for expiry reminder emails (tenant time zone). */
    reminderTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).default('09:00'),
    requireRetakeOnNewVersion: z.boolean().default(false),
    /** Kiosks fetch induction videos on Wi-Fi only. */
    kioskVideoWifiOnly: z.boolean().default(true),
    /** Quiz attempts one person may make per induction per hour on the public channels (contactless, pre-arrival) and the Companion app. 0 = no limit. */
    maxAttemptsPerHour: z.number().int().min(0).default(5),
    /** Uploaded files one owner may hold without attaching them to a document (pre-arrival page, Companion app, portal). 0 = no limit. */
    maxOpenUploads: z.number().int().min(0).default(10),
    /** Visitor induction passes (name and key) are deleted this many days after they expired. 0 = keep. */
    expiredPassRetentionDays: z.number().int().min(0).default(365),
});
export const ComplianceGroupSettings = z.object({
    acceptPendingDocuments: z.boolean().default(false),
    /** Extra people told about expiring documents (besides the member). */
    reminderEmails: z.array(Email).max(10).default([]),
    /** Members may upload their own documents in the Companion app and pre-arrival page. */
    selfUpload: z.boolean().default(true),
});
/**
 * GET /compliance/prearrival/:token (pre-arrival page): one row per document type the visitor's member record is asked for, with its
 * standing today. Empty when the pre-registration has no member record or the group switched self-upload off.
 */
export const PrearrivalDocuments = z.object({
    items: z.array(DocumentType.pick({ id: true, name: true, description: true, needsIssueDate: true, needsExpiry: true, needsFile: true }).extend({
        mode: RequirementMode,
        state: ComplianceStatus.shape.items.element.shape.state,
        expiryDate: LocalDate.nullable(),
    })),
});
//# sourceMappingURL=compliance.js.map